import { Box, Link as MuiLink, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import ReactMarkdown from "react-markdown";

import { CopyButton } from "@components";

import CodeBlock from "./CodeBlock";

const InlineCode = styled("code")(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.grey[900],
  padding: theme.spacing(0.25, 0.75),
  borderRadius: theme.spacing(0.5),
  fontFamily: "'Courier New', monospace",
  fontSize: "0.9rem",
  position: "relative",
  // This extra padding accommodates the copy button
  paddingRight: theme.spacing(3.5),
}));

const StyledCopyButton = styled(CopyButton)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(0.5),
  top: "50%",
  transform: "translateY(-50%)",
  padding: theme.spacing(0.25),
  backgroundColor: theme.palette.grey[200],
  color: theme.palette.grey[800],
  width: "18px",
  height: "18px",
  marginLeft: theme.spacing(0.5),
  "&:hover": {
    backgroundColor: theme.palette.grey[300],
  },
}));

const InlineCodeWrapper = styled(Box)({
  position: "relative",
  display: "inline-block",
  marginLeft: "4px",
  marginRight: "4px",
});

const StyledInlineCode = styled(InlineCode)({
  display: "inline-flex",
  alignItems: "center",
});

const InlineCodeComponent = ({ children }: any) => {
  const codeContent = String(children).replace(/\n$/, "");

  return (
    <InlineCodeWrapper component="span">
      <StyledInlineCode>
        <span>{codeContent}</span>
        <StyledCopyButton
          className="inline-code-copy"
          text={codeContent}
          tooltipText="Copy code"
          size="small"
        />
      </StyledInlineCode>
    </InlineCodeWrapper>
  );
};

const CodeBlockComponent = ({ className, children }: any) => {
  // Single backtick code doesn't have a className, multi-backtick code blocks do
  const isInlineCode = !className;
  if (isInlineCode) {
    return <InlineCodeComponent>{children}</InlineCodeComponent>;
  }

  const match = /language-(\w+)/.exec(className || "");
  const language = match ? match[1] : "text";

  const codeContent = String(children).replace(/\n$/, "");

  return <CodeBlock language={language} code={codeContent} />;
};

const MessageWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isUser",
})<{ isUser: boolean }>(({ theme, isUser }) => ({
  position: "relative",
  padding: theme.spacing(0.5),
  // This extra padding accommodates the copy button
  paddingRight: !isUser ? "36px" : "0",
}));

const StyledMuiLink = styled(MuiLink)(({ theme }) => ({
  wordBreak: "break-all",
  color: theme.palette.primary.main,
  "&:hover": {
    textDecoration: "underline",
  },
}));

const CopyButtonContainer = styled(Box)({
  position: "absolute",
  // This 3 px is because the copy button is a little bit too tall to fit on one line,
  // so this helps it be vertically centered for one line messages
  top: "-3px",
  right: 0,
});

interface MessageContentProps {
  text: string;
  isUser: boolean;
}

const MessageContent: React.FC<MessageContentProps> = ({ text, isUser }) => {
  return (
    <MessageWrapper isUser={isUser}>
      <ReactMarkdown
        components={{
          code: CodeBlockComponent,
          p: ({ children }) => (
            <Typography variant="body1">{children}</Typography>
          ),
          a: ({ href, children }) => (
            <StyledMuiLink
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </StyledMuiLink>
          ),
        }}
      >
        {text}
      </ReactMarkdown>

      {text.length > 20 && !isUser && (
        <CopyButtonContainer>
          <CopyButton
            text={text}
            tooltipText="Copy text"
            size="small"
            className="message-copy-button"
          />
        </CopyButtonContainer>
      )}
    </MessageWrapper>
  );
};

export default MessageContent;
